import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CheckVerifiedIcon from "../../shared/icons/CheckVerifiedIcon";
import DeleteNewIcon from "../../shared/icons/DeleteNewIcon";
import DocumentNewIcon from "../../shared/icons/DocumentNewIcon";
import EditIconNew from "../../shared/icons/EditIconNew";
import Loading from "../../shared/Loading/Loading";
import UploadCloudIcon from "../../shared/icons/UploadCloudIcon";
import CustomButton from "../../custom/custom-button/CustomButton";
import { File, User } from "../../../services";
import { useAmplitude } from "../../../hooks/useAmplitude";
import { useUserData } from "../../../hooks/useUserData";
import { requestErrorDisplay } from "../../../utils";
import { getRequiredDocumentTypes, SOLE_PROPRIETOR } from "../legalTypes";
import style from "./index.module.scss";

const formatDocumentType = (contentType = "") => {
  const mimeMap = {
    "application/pdf": "pdf",
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/webp": "webp",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  };

  return mimeMap[contentType] || contentType.split("/")[1] || "file";
};

const ConfirmDocuments = ({ user, selectedLegalType, onComplete }) => {
  const { t } = useTranslation();
  const { amplitude } = useAmplitude();
  const { refreshUserData } = useUserData();
  const company = useSelector((state) => state.user.company);
  const [listDoc, setListDoc] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState({});
  const [deletingIds, setDeletingIds] = useState({});
  const [dragOverType, setDragOverType] = useState(null);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const isSoleProprietor = selectedLegalType === SOLE_PROPRIETOR;
  const requiredTypes = getRequiredDocumentTypes(selectedLegalType);

  const getDocList = useCallback(async () => {
    if (!user?.id) {
      setListDoc([]);
      return;
    }

    try {
      const response = await File.getDocList(user.id);
      setListDoc(response.data || []);
    } catch (error) {
      console.error(error);
    }
  }, [user?.id]);

  const updateUserData = async () => {
    try {
      await refreshUserData();
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getDocList();
  }, [getDocList]);

  const getDocumentName = (type) => {
    if (type === 0) {
      return isSoleProprietor
        ? t("confirmDocumentsComponent.docNames.ipTalon")
        : t("confirmDocumentsComponent.docNames.regCert");
    }
    if (type === 2) return t("confirmDocumentsComponent.docNames.appointmentOrder");
    return t("confirmDocumentsComponent.docNames.default");
  };

  const handleUpload = async (file, type, existingDocumentId) => {
    if (!file || loadingTypes[type]) return;

    setLoadingTypes((current) => ({ ...current, [type]: true }));
    try {
      if (existingDocumentId) {
        await File.deleteDocument(existingDocumentId);
      }

      amplitude.sendDocsToVerify();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", type);
      formData.append("userId", user.id);

      const response = await File.add(formData);
      if (!response || response.status !== 200) {
        throw new Error("Document upload failed");
      }

      toast.success(t("confirmDocumentsComponent.toast.uploadSuccess"));
      await Promise.all([getDocList(), updateUserData()]);
    } catch (error) {
      requestErrorDisplay(error, t, "confirmDocumentsComponent.toast.uploadError");
    } finally {
      setLoadingTypes((current) => ({ ...current, [type]: false }));
    }
  };

  const handleDelete = async (documentId) => {
    setDeletingIds((current) => ({ ...current, [documentId]: true }));
    try {
      await File.deleteDocument(documentId);
      await getDocList();
    } catch (error) {
      console.error(error);
      toast.error(t("confirmDocumentsComponent.toast.deleteError"));
    } finally {
      setDeletingIds((current) => ({ ...current, [documentId]: false }));
    }
  };

  const handleDrop = (event, type, existingDocumentId) => {
    event.preventDefault();
    setDragOverType(null);
    handleUpload(event.dataTransfer.files[0], type, existingDocumentId);
  };

  const handleComplete = async () => {
    if (isCompleting) return;

    setIsCompleteModalOpen(true);
    setIsCompleting(true);
    try {
      await User.update({
        name: user.name,
        surname: user.surname,
        phoneNumber: user.phoneNumber,
        company: {
          name: company?.name,
          bin: company?.bin,
          legalType: selectedLegalType,
        },
      });
    } catch (error) {
      setIsCompleteModalOpen(false);
      requestErrorDisplay(error, t);
    } finally {
      setIsCompleting(false);
    }
  };

  const documentsByType = new Map(listDoc.map((document) => [document.type, document]));
  const hasRequiredDocuments = requiredTypes.every((type) => documentsByType.has(type));

  return (
    <section className={style.card}>
      <p className={style.sectionLabel}>{t("confirmDocumentsComponent.sectionLabel")}</p>

      <div className={style.documents}>
        {requiredTypes.map((type) => {
          const document = documentsByType.get(type);

          if (document) {
            return (
              <div className={style.fileItem} key={type}>
                <div className={style.fileItem_icon}>
                  <DocumentNewIcon />
                </div>
                <div className={style.fileItem_content}>
                  <div className={style.fileItem_name}>
                    {getDocumentName(type)}.{formatDocumentType(document.contentType)}
                  </div>
                  <div className={style.fileItem_size}>{t("confirmDocumentsComponent.fileStatus.uploaded")}</div>
                </div>
                {user.state !== "Confirmed" ? (
                  <div className={style.fileActions}>
                    <label className={style.iconButton} aria-label={t("confirmDocumentsComponent.buttons.replaceFile")}>
                      <input
                        type="file"
                        onChange={(event) => handleUpload(event.target.files[0], type, document.id)}
                      />
                      <EditIconNew width={24} height={24} />
                    </label>
                    {deletingIds[document.id] ? (
                      <Loading inBlock marginBottom={0} />
                    ) : (
                      <button
                        className={style.iconButton}
                        type="button"
                        aria-label={t("confirmDocumentsComponent.buttons.deleteFile")}
                        onClick={() => handleDelete(document.id)}
                      >
                        <DeleteNewIcon />
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            );
          }

          return (
            <label
              key={type}
              className={cn(style.drop_box, { [style.dragOver]: dragOverType === type })}
              onDragOver={(event) => {
                event.preventDefault();
                setDragOverType(type);
              }}
              onDragLeave={() => setDragOverType(null)}
              onDrop={(event) => handleDrop(event, type)}
            >
              <input type="file" onChange={(event) => handleUpload(event.target.files[0], type)} />
              <div className={style.drop_icon}>
                {loadingTypes[type] ? <Loading inBlock marginBottom={0} /> : <UploadCloudIcon />}
              </div>
              <div className={style.drop_content}>
                <span className={style.drop_title}>{getDocumentName(type)}</span>
                <span className={style.drop_textUpload}>
                  <span className={style.drop_uploadFile}>{t("confirmDocumentsComponent.buttons.uploadFile")}</span>
                  <span className={style.drop_boxText}>{t("confirmDocumentsComponent.dropZone.dragOrDrop")}</span>
                </span>
              </div>
            </label>
          );
        })}
      </div>

      <div className={style.continueButton}>
        <CustomButton
          text={t("common.continue")}
          isFall
          isLoading={isCompleting}
          isDisabled={isCompleting || !selectedLegalType || !hasRequiredDocuments}
          onClick={handleComplete}
        />
      </div>

      {isCompleteModalOpen ? (
        <div className={cn("modal-overlay", style.modal)}>
          <div className={style.modal_content} role="dialog" aria-modal="true">
            <h2 className={style.modal_title}>{t("confirmDocumentsComponent.completeModal.title")}</h2>
            <div className={style.modal_successIcon}>
              <CheckVerifiedIcon width={40} height={40} />
            </div>
            <p className={style.modal_description}>{t("confirmDocumentsComponent.completeModal.text")}</p>
            <CustomButton
              text={t("confirmDocumentsComponent.completeModal.button")}
              isFall
              isLoading={isCompleting}
              isDisabled={isCompleting}
              disabled={isCompleting}
              onClick={() => {
                setIsCompleteModalOpen(false);
                onComplete?.();
              }}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ConfirmDocuments;
